import { isEqual } from 'lodash';
import type { JsonValue, StoryEditChange, StoryEditTarget, StoryPropertyPatch } from './types';

/** One authored document before and after a write; the change is the patch between them. */
export type PropertyChangeDocument = {
	target: StoryEditTarget;
	before: unknown;
	after: unknown;
};

/** Expected capture refusal: the whole action becomes "unsupported" instead of breaking the local edit. */
export class StoryEditCaptureError extends Error {}

const FORBIDDEN_KEYS = ['__proto__', 'prototype', 'constructor'];

/** Plain object model; anything else is a leaf compared as a whole. */
const isPlainObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && Object.getPrototypeOf(value) === Object.prototype;

const hasOwn = (value: object, key: string) => Object.prototype.hasOwnProperty.call(value, key);

/** Values JSON leaves out of objects (and turns into null in arrays). */
const isAbsent = (value: unknown) => value === undefined || typeof value === 'function' || typeof value === 'symbol';

/** Own member, with values JSON leaves out treated as absent. */
const memberOf = (value: Record<string, unknown>, key: string) => {
	const member = hasOwn(value, key) ? value[key] : undefined;
	return isAbsent(member) ? undefined : member;
};

/** RFC 6901 JSON Pointer: `~` → `~0`, `/` → `~1`. Prototype keys are never authored data. */
function toPointer(path: string[]): string {
	if (path.some(key => FORBIDDEN_KEYS.includes(key))) throw new StoryEditCaptureError('Invalid property path');
	return `/${path.map(key => key.replace(/~/g, '~0').replace(/\//g, '~1')).join('/')}`;
}

/**
 * Deep-copies a model into the JSON a save request carries, which also detaches it from later mutations.
 * Follows `JSON.stringify`: `toJSON` is applied (dates become strings), non-finite numbers become null,
 * and undefined or function members are left out of objects and become null in arrays.
 */
export function toJsonValue(value: unknown): JsonValue {
	if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
	if (typeof value === 'number') return Number.isFinite(value) ? value : null;
	if (typeof value !== 'object') throw new StoryEditCaptureError(`A ${typeof value} value cannot be saved`);
	if ('toJSON' in value && typeof value.toJSON === 'function') return toJsonValue(value.toJSON());
	if (Array.isArray(value)) return value.map(item => (isAbsent(item) ? null : toJsonValue(item)));
	return Object.fromEntries(
		Object.entries(value)
			.filter(([, member]) => !isAbsent(member))
			.map(([key, member]) => [key, toJsonValue(member)])
	);
}

/**
 * Deep equality of leaf values and arrays. Serialization is the fast path; the structural check
 * catches equal objects whose keys come in another order (the server stores keys sorted).
 */
const isSameValue = (before: unknown, after: unknown) =>
	JSON.stringify(before) === JSON.stringify(after) || isEqual(before, after);

/**
 * Appends the smallest add/replace/remove operations that turn `before` into `after` at `path`.
 * Objects are compared member by member; arrays and leaf values are replaced whole.
 * Works on the models directly: only emitted values are converted to JSON.
 */
function diff(before: unknown, after: unknown, path: string[], patch: StoryPropertyPatch[]): void {
	// Immer keeps unchanged subtrees by reference, so most of a large document is skipped here.
	if (before === after) return;

	if (isPlainObject(before) && isPlainObject(after)) {
		for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
			const childPath = [...path, key];
			const beforeMember = memberOf(before, key);
			const afterMember = memberOf(after, key);
			if (afterMember === undefined) {
				if (beforeMember !== undefined) patch.push({ op: 'remove', path: toPointer(childPath) });
			} else if (beforeMember === undefined) {
				patch.push({ op: 'add', path: toPointer(childPath), value: toJsonValue(afterMember) });
			} else {
				diff(beforeMember, afterMember, childPath, patch);
			}
		}
		return;
	}

	if (isSameValue(before, after)) return;
	if (!path.length) throw new StoryEditCaptureError('Target documents cannot be replaced');
	patch.push({ op: 'replace', path: toPointer(path), value: toJsonValue(after) });
}

/** One change per document that differs; documents keep their order. */
export function capturePropertyChanges(documents: PropertyChangeDocument[]): StoryEditChange[] {
	const changes: StoryEditChange[] = [];
	for (const { target, before, after } of documents) {
		const patch: StoryPropertyPatch[] = [];
		diff(before, after, [], patch);
		if (patch.length) changes.push({ target: structuredClone(target), patch });
	}
	return changes;
}
